import axios from 'axios';

/*
 * Metrics API.
 *
 * Note the header is `headers` (plural). rtkData.api.js uses `header:` (singular),
 * which RTK Query ignores — those calls only authenticate because
 * authenticateCheck falls back to the session cookie or a body-supplied token.
 * These endpoints resolve identity from the VERIFIED token only, so the header
 * has to actually arrive.
 */

/*
 * Base URL, resolved exactly the way rtkData.api.js does it.
 *
 * NOT from REACT_APP_API_SERVER_IP — that points at the deployed environment
 * (https://dev.lavenirai.com) even when REACT_APP_DEPLOY is "local", so using it
 * would silently send a developer's requests to the shared dev server instead of
 * the API they are running.
 */
const API_BASE = () => {
  let host = process.env.REACT_APP_BASE_URL_LOC;
  if (process.env.REACT_APP_MODE === 'docker') {
    host = process.env.REACT_APP_BASE_URL_DOC;
  }
  let base = `http://${host}`;
  if (process.env.REACT_APP_DEPLOY === 'nginx') {
    base += '/api';
  } else if (process.env.REACT_APP_DEPLOY === 'local') {
    base += `:${process.env.REACT_APP_SERVER_PORT}/api`;
  }
  return base;
};

const post = (path, idToken, body = {}) =>
  axios.post(`${API_BASE()}${path}`, body, {
    headers: { authtoken: idToken, 'Content-Type': 'application/json' },
    withCredentials: true,
  });

/** Unwrap the { data, error } envelope, throwing on a logical error. */
const unwrap = (res) => {
  const payload = res && res.data;
  if (!payload) throw new Error('Empty response');
  if (payload.error && payload.error.isError === '1') {
    throw new Error(payload.error.message || 'Request failed');
  }
  return payload.data;
};

/** Level, chapter ring, practice table and quiz table for one learner. */
/**
 * `targetUserIds` carries every region's id for the same person.
 *
 * Each region mints its own, so the id a roster row shows may belong to a
 * region this server has no record of — an admin on SG opening somebody whose
 * only account is in the US. Sending all of them lets the server ask whichever
 * region actually holds them.
 */
export const fetchLearnerOverview = async (idToken, targetUserId, targetUserIds) =>
  unwrap(
    await post('/metrics/learner/overview', idToken, {
      user: {},
      ...(targetUserId ? { targetUserId } : {}),
      ...(targetUserIds && targetUserIds.length ? { targetUserIds } : {}),
    })
  );

/**
 * Sparse daily activity for the calendar. Sends `targetUserIds` for the same
 * reason the overview does: without them a person opened from a region that
 * has no record of the clicked id is looked up in one region only, and the
 * calendar disagrees with the merged figures above it.
 */
export const fetchLearnerActivity = async (idToken, targetUserId, timezone, targetUserIds) =>
  unwrap(
    await post('/metrics/learner/activity', idToken, {
      user: {},
      ...(targetUserId ? { targetUserId } : {}),
      ...(targetUserIds && targetUserIds.length ? { targetUserIds } : {}),
      timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    })
  );

/** Full attempt history for one module — fetched only when a row is expanded. */
export const fetchModuleAttempts = async (idToken, moduleIdSst, targetUserId, targetUserIds) =>
  unwrap(
    await post('/metrics/learner/module/attempts', idToken, {
      user: {},
      moduleIdSst,
      ...(targetUserId ? { targetUserId } : {}),
      ...(targetUserIds && targetUserIds.length ? { targetUserIds } : {}),
    })
  );



/* ------------------------------------------------------------------ *
 * cohort v2
 *
 * Three calls rather than one, matching the endpoints: the org list is
 * unaffected by anything on the page, the overview changes with the date range,
 * and activity is pinned to a fixed 12 months so its trend does not collapse to
 * whatever range is selected above it.
 * ------------------------------------------------------------------ */

const TZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/**
 * One organisation's overview.
 *
 * `to` is the CLIENT's clock, not the server's — without it a manager outside
 * the server's timezone gets a window ending yesterday. The server validates and
 * rejects rather than clamping, so a bad range surfaces as an error instead of
 * silently reporting a different period than the one asked for.
 */
export const fetchCohortOverview = async (idToken, { organizationId, organizationIds, from, to, sinceStart, regions } = {}) =>
  unwrap(
    await post('/metrics/cohort/overview', idToken, {
      user: {},
      ...(organizationId ? { organizationId } : {}),
      // Every region's id for this organisation. Each region mints its own, so
      // sending only the clicked one matches in that region and comes back
      // empty from the others.
      ...(organizationIds && organizationIds.length ? { organizationIds } : {}),
      // `sinceStart` MUST be forwarded. Dropping it was a silent bug: the server
      // then saw neither sinceStart nor from, defaulted to `to - 30 days`, and
      // the "Since start" range reported the last month while labelling itself
      // "30 days" — a wrong answer that looked like a right one.
      ...(sinceStart ? { sinceStart: true } : {}),
      ...(from != null ? { from } : {}),
      // Sent to the server, not applied to the rows: a region selection has to
      // narrow adoption and the member count too, not just the table.
      ...(regions && regions.length ? { regions } : {}),
      to: to != null ? to : Date.now(),
      timezone: TZ(),
    })
  );

/** 12 months of pooled days and weekly active users. */
export const fetchCohortActivity = async (idToken, { organizationId, organizationIds } = {}) =>
  unwrap(
    await post('/metrics/cohort/activity', idToken, {
      user: {},
      ...(organizationId ? { organizationId } : {}),
      // Every region's id: the calendar has to cover the same people the
      // figures above it do.
      ...(organizationIds && organizationIds.length ? { organizationIds } : {}),
      timezone: TZ(),
    })
  );

/* ------------------------------------------------------------------ *
 * anonymity settings — admin only
 * ------------------------------------------------------------------ */

/**
 * Every organisation, the regions it exists in, and whether reporting is
 * anonymised in each.
 *
 * Admin only; the server 403s anyone else. It used to be the signed-in
 * manager's own preference, which let a manager put their own organisation's
 * names back on — a preference rather than a commitment.
 */
export const fetchAnonymitySettings = async (idToken) =>
  unwrap(await post('/metrics/preferences', idToken, { user: {} }));

/**
 * Set one organisation's anonymity in one region.
 *
 * One cell per call on purpose: the regions exist because different countries
 * have different expectations about naming employees, so a single switch for
 * the whole organisation would not be worth having.
 */
export const saveAnonymitySetting = async (idToken, { organizationId, region, anonymous }) =>
  unwrap(
    await post('/metrics/preferences/update', idToken, {
      user: {},
      organizationId,
      region,
      anonymous,
    })
  );

/* ------------------------------------------------------------------ *
 * admin console — every organisation at once
 * ------------------------------------------------------------------ */

/**
 * Portfolio, accounts, rankings, efficacy. Admin only; the server 403s anyone else.
 *
 * `regions` is sent to the SERVER rather than applied to the rows afterwards,
 * because it changes every figure on the page. Filtering the table client-side
 * while the summary tiles still described the whole platform would put two
 * different answers on one screen.
 */
export const fetchAdminOverview = async (idToken, { from, to, sinceStart, regions, includeExpired } = {}) =>
  unwrap(
    await post('/metrics/admin/overview', idToken, {
      user: {},
      ...(sinceStart ? { sinceStart: true } : {}),
      ...(from != null ? { from } : {}),
      ...(regions && regions.length ? { regions } : {}),
      // Internal and test orgs are always left out; expired ones unless this is set.
      ...(includeExpired ? { includeExpired: true } : {}),
      to: to != null ? to : Date.now(),
      timezone: TZ(),
    })
  );

/** Case studies, avatars, quizzes and the chapter/lesson catalogue, pooled. */
export const fetchAdminContent = async (idToken, { from, to, sinceStart, includeExpired } = {}) =>
  unwrap(
    await post('/metrics/admin/content', idToken, {
      user: {},
      ...(sinceStart ? { sinceStart: true } : {}),
      ...(from != null ? { from } : {}),
      ...(includeExpired ? { includeExpired: true } : {}),
      to: to != null ? to : Date.now(),
      timezone: TZ(),
    })
  );
